import { useNavigate } from "@tanstack/react-router";
import { Button, Tag } from "antd";
import { useRequestAccessMutation } from "api/Tenant/hooks";
import { AxiosError } from "axios";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useState } from "react";
import { toast } from "react-toastify";
import { CatalogStatus } from "types/enums";
import { ApiErrorResponse } from "types/models";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";
import { getTagDefaultColor } from "utils/getStatusDefaultBackground";

import TenantData = App.Data.TenantData;

export const useProgramAccessAction = (onRequestSuccess?: () => void) => {
    const { t } = usePaldockTranslation("tables.programs-catalog");
    const { t: tStatus } = usePaldockTranslation("general");
    const navigate = useNavigate();

    const [awaitingApprovalTenant, setAwaitingApprovalTenant] = useState<TenantData | null>(null);
    const [pendingTenantId, setPendingTenantId] = useState<string | null>(null);

    const requestAccessMutation = useRequestAccessMutation();

    const handleRequestAccess = (record: TenantData) => {
        setPendingTenantId(record.id);
        requestAccessMutation.mutate(
            { tenantId: record.id },
            {
                onSuccess: (data) => {
                    if (data.joined) {
                        toast.success(t("messages.joined"));
                        navigate({ to: "/$tenantSlug", params: { tenantSlug: record.slug_name } });
                    } else {
                        toast.success(t("messages.request_submitted"));
                        setAwaitingApprovalTenant(record);
                    }
                    onRequestSuccess?.();
                },
                onError: (error: AxiosError<ApiErrorResponse>) =>
                    toast.error(extractValidationErrorMessages(error)[0] ?? t("messages.error")),
                onSettled: () => setPendingTenantId(null),
            }
        );
    };

    const renderAccessAction = (record: TenantData, size: "small" | "middle" | "large" = "small") => {
        if (record.catalog_status === CatalogStatus.Member) {
            return (
                <Button
                    type="default"
                    size={size}
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate({ to: "/$tenantSlug", params: { tenantSlug: record.slug_name } });
                    }}
                >
                    {t("actions.go_to_program")}
                </Button>
            );
        }

        if (record.catalog_status === CatalogStatus.Pending) {
            return <Tag color={getTagDefaultColor("pending")}>{tStatus("request_status.pending")}</Tag>;
        }

        const isRequestingThisTenant = requestAccessMutation.isPending && pendingTenantId === record.id;

        if (record.catalog_status === CatalogStatus.DifferentRole) {
            return (
                <Button
                    type="primary"
                    size={size}
                    loading={isRequestingThisTenant}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRequestAccess(record);
                    }}
                >
                    {t("actions.request_role")}
                </Button>
            );
        }

        return (
            <Button
                type="primary"
                size={size}
                loading={isRequestingThisTenant}
                onClick={(e) => {
                    e.stopPropagation();
                    handleRequestAccess(record);
                }}
            >
                {t("actions.request_access")}
            </Button>
        );
    };

    return { renderAccessAction, awaitingApprovalTenant, setAwaitingApprovalTenant, t };
};
